// Notes composer (mock s26): dashed blue-tinted card — multiline input, Enter
// saves (Shift+Enter = newline), 📌 נעיצה toggle applied to the note being
// composed, and the 🎤 voice-note recorder. The pin toggle applies to BOTH a
// typed note and a voice note recorded while it's on.
import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, InputBase, Stack, Tooltip } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { PushPin, PushPinOutlined } from "@material-ui/icons";
import { useSnackbar } from "notistack5";
import { VoiceNoteRecorder } from "./VoiceNoteRecorder";
import type { AddNoteOptions } from "./useContactNotes";

type NoteComposerProps = {
  contactId: string;
  addNote: (text: string, options?: AddNoteOptions) => Promise<void>;
};

export function NoteComposer({ contactId, addNote }: NoteComposerProps): JSX.Element {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [text, setText] = useState("");
  const [pinned, setPinned] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Voice saves fire from a MediaRecorder onstop closure created at recording
  // START — read the pin state through a ref so toggling נעיצה mid-recording
  // still applies to the note being saved.
  const pinnedRef = useRef(false);
  useEffect(() => {
    pinnedRef.current = pinned;
  }, [pinned]);

  const handleSaveText = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Optimistic: clear immediately, restore on failure.
    setText("");
    setPinned(false);
    addNote(trimmed, { pinned }).catch(() => {
      setText(trimmed);
      enqueueSnackbar("שמירת ההערה נכשלה", { variant: "error", autoHideDuration: 4000 });
    });
    inputRef.current?.focus();
  }, [text, pinned, addNote, enqueueSnackbar]);

  // The recorder handles its own failure snackbar; a rejected addNote must
  // still reject so the recorder knows the save failed.
  const handleSaveVoice = useCallback(
    async ({ audioUrl, transcription }: { audioUrl: string; transcription: string }) => {
      await addNote(transcription, { pinned: pinnedRef.current, audioUrl });
      setPinned(false);
    },
    [addNote]
  );

  const accent = theme.palette.info.main;

  // Recording REPLACES what's in the box (the transcription becomes the note
  // text), so offering the mic on top of a half-written note invites losing it.
  // Trimmed, so a stray space doesn't grey out the recorder — and it flips back
  // the moment the field is cleared, including after a successful save.
  const hasText = text.trim().length > 0;

  // `dir="auto"` resolves direction from the VALUE — while the field is empty
  // there is nothing to resolve, so it falls back to LTR and the Hebrew
  // placeholder hugs the left edge (glaring on mobile). Anchor the empty field
  // to the UI direction, and hand control back to `auto` the moment there is
  // text so a pasted LTR value (phone number, URL) still renders LTR.
  const composerDir = hasText ? "auto" : theme.direction;

  return (
    <Box
      onClick={() => inputRef.current?.focus()}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        // Wrap instead of crushing the input: on a narrow phone the recorder +
        // נעיצה drop to their own line rather than squeezing the text field to
        // a few characters. Same technique as the tasks quick-add — CSS wrap
        // rather than a useMediaQuery breakpoint, so it degrades continuously
        // and a wide desktop row stays exactly as it was (single line).
        flexWrap: "wrap",
        px: 2,
        py: 1.25,
        borderRadius: 1.5,
        border: `1.5px dashed ${alpha(accent, 0.45)}`,
        bgcolor: alpha(accent, 0.04),
        cursor: "text",
        mb: 2
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          mt: 0.25,
          borderRadius: 1,
          bgcolor: accent,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 800,
          fontSize: 15,
          flex: "none"
        }}
      >
        +
      </Box>

      <InputBase
        inputRef={inputRef}
        multiline
        maxRows={5}
        value={text}
        placeholder="כתבו הערה חדשה… Enter לשמירה"
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSaveText();
          }
        }}
        inputProps={{ dir: composerDir, "aria-label": "הערה חדשה" }}
        // `fullWidth` (width:100%) would make the flex BASE size the full row,
        // so a wrapping container would break the line at every width. A
        // growable 180px basis fills the row on desktop exactly as before, and
        // the 120px floor is what tips the controls onto line 2 on a phone.
        sx={{ fontSize: 14, pt: 0.25, flex: "1 1 180px", minWidth: 120 }}
      />

      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        // Logical margin — keeps the controls at the row's end in RTL without
        // stylis flipping it, and pins them to the end of their OWN line once
        // the row wraps (same trick as tasks/TaskQuickAdd + calls/ContactCallRow).
        sx={{ flexShrink: 0, marginInlineStart: "auto" }}
        onClick={(event) => event.stopPropagation()}
      >
        <VoiceNoteRecorder
          contactId={contactId}
          onSave={handleSaveVoice}
          disabled={hasText}
          disabledReason="נקו את הטקסט כדי להקליט"
        />
        <Tooltip title={pinned ? "ההערה תישמר נעוצה — ביטול בלחיצה" : "נעיצת ההערה הבאה"}>
          <Button
            size="small"
            color="inherit"
            onClick={() => setPinned((prev) => !prev)}
            startIcon={
              pinned ? (
                <PushPin sx={{ fontSize: 16 }} />
              ) : (
                <PushPinOutlined sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              whiteSpace: "nowrap",
              flexShrink: 0,
              color: pinned ? "warning.dark" : "text.secondary",
              bgcolor: pinned ? alpha(theme.palette.warning.main, 0.16) : "transparent"
            }}
            aria-pressed={pinned}
          >
            נעיצה
          </Button>
        </Tooltip>
      </Stack>
    </Box>
  );
}
